import './coapplicantDifferentResidencePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/shared_new/AddressLineOne/AddressLineOne';
import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { USStates } from '../../../constants/states';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: {
      coapplicantCurrentResidenceCityAsString,
      coapplicantCurrentResidenceLineOneAsString,
      coapplicantCurrentResidenceLineTwoAsString,
      coapplicantCurrentResidencePostalCodeAsString,
      coapplicantCurrentResidenceStateAsString,
      isCoapplicantPersisted,
    },
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceCity,
    setCoapplicantCurrentResidenceLineOne,
    setCoapplicantCurrentResidenceLineTwo,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    setCoapplicantCurrentResidencePostalCode,
    setCoapplicantCurrentResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: coapplicantCurrentResidenceCityAsString,
      housingAmountValue: inputEventHandler.currency.formatter(
        coapplicantCurrentResidenceMonthlyPaymentAmount,
      ),
      lineOneValue: coapplicantCurrentResidenceLineOneAsString,
      lineTwoValue: coapplicantCurrentResidenceLineTwoAsString,
      postalCodeValue: coapplicantCurrentResidencePostalCodeAsString,
      stateValue: coapplicantCurrentResidenceStateAsString,
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      housingAmountValue,
      lineOneValue,
      lineTwoValue,
      postalCodeValue,
      stateValue,
    } = values;

    if (
      !cityValue ||
      !housingAmountValue ||
      !lineOneValue ||
      !postalCodeValue ||
      !stateValue
    ) {
      return;
    }

    setCoapplicantCurrentResidenceCity(cityValue);
    setCoapplicantCurrentResidenceLineOne(lineOneValue);
    setCoapplicantCurrentResidenceLineTwo(lineTwoValue);
    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCoapplicantCurrentResidencePostalCode(postalCodeValue);
    setCoapplicantCurrentResidenceState(stateValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-different-residence-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantDifferentResidence.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantDifferentResidence.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantDifferentResidence.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantDifferentResidence.progress.root
            }
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantDifferentResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantDifferentResidence.form.h1
                }
              >
                {t('CoapplicantDifferentResidencePage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantDifferentResidence.form.h2
                }
              >
                {t('CoapplicantDifferentResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantDifferentResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    error={!isEmpty(errors.housingAmountValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantDifferentResidence.form
                          .amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('CoapplicantDifferentResidencePage.housingAmount')}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantDifferentResidence.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.coapplicantDifferentResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantDifferentResidence.form
                          .addressLine1Input,
                    }}
                    label={t(
                      'CoapplicantDifferentResidencePage.residenceLineOne',
                    )}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantDifferentResidence.form
                              .addressLine2Input,
                        }}
                        label={t(
                          'CoapplicantDifferentResidencePage.residenceLineTwo',
                        )}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('cityValue', cityValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('cityValue')),
                        }}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.cityValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantDifferentResidence.form
                              .cityInput,
                        }}
                        label={t(
                          'CoapplicantDifferentResidencePage.residenceCity',
                        )}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.cityValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantDifferentResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.cityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('stateValue', stateValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('stateValue')),
                        }}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        autoComplete="address-level1"
                        error={!isEmpty(errors.stateValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantDifferentResidence.form
                              .stateSelect,
                        }}
                        label={t(
                          'CoapplicantDifferentResidencePage.residenceState',
                        )}
                        value={watch(
                          'stateValue',
                          coapplicantCurrentResidenceStateAsString,
                        )}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t(
                            'CoapplicantDifferentResidencePage.residenceState',
                          )}
                        </MenuItem>

                        {USStates.map(({ abbreviation, name }) => {
                          return (
                            <MenuItem key={abbreviation} value={abbreviation}>
                              {name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.stateValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantDifferentResidence.form
                              .stateSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.stateValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('postalCodeValue', postalCodeValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('postalCodeValue')),
                        }}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.postalCodeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.coapplicantDifferentResidence.form
                              .zipCodeInput,
                          inputMode: 'numeric',
                        }}
                        label={t('CoapplicantDifferentResidencePage.zip')}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.postalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantDifferentResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.postalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantDifferentResidence.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantDifferentResidencePage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantDifferentResidence.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantDifferentResidencePage';
